import type { MarketplaceId } from "@nova-ai/shared"

type MarketplaceMeta = {
  /** Brand label for display + favicon fallback initial. */
  brand: string
  /** Favicon domain (brand site) for the logo, or null to use the fallback. */
  logoDomain: string | null
  /** Stable bundled logo when a remote favicon is unreliable. */
  logoPath?: string
  /** ISO 3166-1 alpha-2 listing country, for the corner flag. */
  country: string
}

// The marketplace id is opaque, so display metadata (brand, favicon domain,
// country) is looked up by the WHOLE id here rather than parsed out of the
// string. Mirrors each marketplace's config.json `brand`/`country`. Unknown
// ids fall back to a neutral mark.
const MARKETPLACE_META: Record<string, MarketplaceMeta> = {
  "empik-pl": { brand: "Empik", logoDomain: "empik.com", country: "pl" },
  // Official mark: https://www.benufarma.it/assets/favicon/icon.png (2026-10-20).
  "benu-it": {
    brand: "BENU",
    logoDomain: null,
    logoPath: "/brand/benu-mark.png",
    country: "it",
  },
  "drmax-sk": { brand: "Dr. Max", logoDomain: "drmax.sk", country: "sk" },
  "decathlon-it": { brand: "Decathlon", logoDomain: "decathlon.com", country: "it" },
  "decathlon-cz": { brand: "Decathlon", logoDomain: "decathlon.com", country: "cz" },
  "decathlon-de": { brand: "Decathlon", logoDomain: "decathlon.com", country: "de" },
  "decathlon-fr": { brand: "Decathlon", logoDomain: "decathlon.com", country: "fr" },
  "decathlon-pl": { brand: "Decathlon", logoDomain: "decathlon.com", country: "pl" },
  "shop-apotheke-it": { brand: "Shop Apotheke", logoDomain: "shop-apotheke.com", country: "it" },
  "shop-apotheke-de": { brand: "Shop Apotheke", logoDomain: "shop-apotheke.com", country: "de" },
  "shop-apotheke-at": { brand: "Shop Apotheke", logoDomain: "shop-apotheke.com", country: "at" },
  "cdiscount-fr": { brand: "Cdiscount", logoDomain: "cdiscount.com", country: "fr" },
  "greenweez-fr": { brand: "Greenweez", logoDomain: "greenweez.com", country: "fr" },
  "leclerc-fr": { brand: "E.Leclerc", logoDomain: "e.leclerc", country: "fr" },
  "carrefour-fr": { brand: "Carrefour", logoDomain: "carrefour.fr", country: "fr" },
  "eprice-it": {
    brand: "ePrice",
    logoDomain: null,
    logoPath: "/brand/eprice-mark.svg",
    country: "it",
  },
  "mimovrste-si": { brand: "Mimovrste", logoDomain: "mimovrste.com", country: "si" },
}

export function marketplaceMeta(marketplace: MarketplaceId): MarketplaceMeta {
  return MARKETPLACE_META[marketplace] ?? { brand: marketplace, logoDomain: null, country: "" }
}
